import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import type { HistoryState } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { Box } from "@mui/material";
import { type FC, type ReactElement, useCallback } from "react";
import { useLexicalContext } from "./lexical-context";
import { useFocusEditor } from "./utils/use-focus-editor";

export type LexicalEditorProps = {
  format: "email" | "chat" | "sms";
  renderLayout: (props: ReactElement) => ReactElement;
  slots: {
    contentEditable: (data: {
      focusEditor: () => void;
      onRef: (x: HTMLDivElement) => void;
    }) => ReactElement;
    placeholder?: ReactElement;
    plugins?: {
      head?: () => ReactElement;
      body?: (historyState: HistoryState) => ReactElement;
    };
  };
};

export const LexicalEditor: FC<LexicalEditorProps & { historyState: HistoryState }> = ({
  format,
  renderLayout,
  slots,
  historyState,
}) => {
  const focusEditor = useFocusEditor();
  const {
    floatingAnchor: { setFloatingAnchorElem },
  } = useLexicalContext();

  const onRef = useCallback(
    (_floatingAnchorElem: HTMLDivElement): void => {
      setFloatingAnchorElem(_floatingAnchorElem);
    },
    [setFloatingAnchorElem],
  );

  return (
    <>
      <Box sx={{ border: "none", position: "relative" }}>
        {slots.plugins?.head?.()}
        {renderLayout(
          <>
            <Box
              className="editor-inner"
              sx={{
                border: "none",
                minHeight: "150px",
                resize: "vertical",
                height: "100%",
                alignItems: "stretch",
                position: "relative",
              }}
            >
              {format === "sms" ? (
                <PlainTextPlugin
                  contentEditable={slots.contentEditable({ focusEditor, onRef })}
                  ErrorBoundary={LexicalErrorBoundary}
                  placeholder={slots.placeholder}
                />
              ) : (
                <RichTextPlugin
                  contentEditable={slots.contentEditable({ focusEditor, onRef })}
                  ErrorBoundary={LexicalErrorBoundary}
                  placeholder={slots.placeholder}
                />
              )}
              {slots.plugins?.body?.(historyState)}
            </Box>
          </>,
        )}
      </Box>
    </>
  );
};
